import type { Mandate, Profile, ProposedAction } from './domain';

export type ViewId =
  | 'overview'
  | 'cockpit'
  | 'companion'
  | 'vault'
  | 'wiki'
  | 'scout'
  | 'creator'
  | 'assets'
  | 'attention'
  | 'wallet'
  | 'governance';

export type ModuleDefinition = {
  id: ViewId;
  label: string;
  shortLabel: string;
  description: string;
  status: 'live' | 'shadow' | 'planned';
};

export const modules: ModuleDefinition[] = [
  { id: 'overview', label: 'Command Center', shortLabel: 'Home', description: 'One governed view of your personal agency.', status: 'live' },
  { id: 'cockpit', label: 'Unified Cockpit', shortLabel: 'Cockpit', description: 'One input surface for Personal, Work, World and Build.', status: 'live' },
  { id: 'companion', label: 'Companion', shortLabel: 'Companion', description: 'Message-first access, consented follow-up and governed action proposals.', status: 'live' },
  { id: 'vault', label: 'Personal Vault', shortLabel: 'Vault', description: 'Identity, documents, preferences, consent and credentials.', status: 'shadow' },
  { id: 'wiki', label: 'Living Wiki', shortLabel: 'Wiki', description: 'A source-linked memory and knowledge graph you own.', status: 'shadow' },
  { id: 'scout', label: 'Speider + BARO', shortLabel: 'Scout', description: 'Find opportunities, filter noise and rank what matters.', status: 'shadow' },
  { id: 'creator', label: 'Creator Studio', shortLabel: 'Create', description: 'Produce and publish video, music, podcasts and social content.', status: 'shadow' },
  { id: 'assets', label: 'Asset Manager', shortLabel: 'Assets', description: 'Discover underused time, knowledge, rights and physical assets.', status: 'shadow' },
  { id: 'attention', label: 'Attention Market', shortLabel: 'Attention', description: 'Control who may reach you, why, and under which terms.', status: 'planned' },
  { id: 'wallet', label: 'Agent Wallet', shortLabel: 'Wallet', description: 'Budgeted, mandate-bound payments and agent-to-agent purchasing.', status: 'shadow' },
  { id: 'governance', label: 'Governance', shortLabel: 'Control', description: 'Mandates, step-up approvals, revocation and receipt evidence.', status: 'live' },
];

export const profileContent: Record<Profile, {
  name: string;
  subtitle: string;
  objective: string;
  balanceMinor: number;
  currency: string;
}> = {
  personal: {
    name: 'Personal profile',
    subtitle: 'Private life, family, finances and personal development',
    objective: 'Protect time, reduce waste and improve long-term optionality.',
    balanceMinor: 1842500,
    currency: 'NOK',
  },
  professional: {
    name: 'Professional profile',
    subtitle: 'Work, IP, publishing, network and commercial activity',
    objective: 'Turn verified knowledge into useful products and durable income.',
    balanceMinor: 428500,
    currency: 'NOK',
  },
};

const sharedMandate = {
  principalId: 'human-owner',
  agentId: 'personal-twin',
  maxSpendMinor: 50000,
  maxCumulativeSpendMinor: 150000,
  spentMinor: 24900,
  currency: 'NOK',
  validUntil: '2027-07-24T00:00:00.000Z',
  allowedRecipients: ['higgsfield', 'suno', 'spotify', 'youtube', 'openai', 'cloudflare'],
  allowedChannels: ['private', 'linkedin', 'youtube', 'spotify', 'website'],
  allowedDataScopes: ['public-profile', 'professional-knowledge', 'content-drafts', 'payment-metadata'],
  requiresEvidence: true,
  requiresApprovalAboveMinor: 10000,
};

export const mandates: Record<Profile, Mandate> = {
  personal: {
    ...sharedMandate,
    id: 'mandate-personal-001',
    profile: 'personal',
    capabilities: ['READ_MEMORY', 'WRITE_MEMORY', 'SPEND_MONEY', 'BUY_SERVICE', 'BOOK_SERVICE', 'MANAGE_ASSET', 'MANAGE_ATTENTION'],
    allowedPurposes: ['Renew essential subscription', 'Book approved travel', 'Reduce unused subscriptions'],
    allowedDataScopes: ['private-profile', 'payment-metadata', 'calendar-availability'],
    allowedRecipients: ['cloudflare', 'openai'],
    allowedChannels: ['private'],
    maxSpendMinor: 20000,
    maxCumulativeSpendMinor: 60000,
    spentMinor: 11900,
    requiresApprovalAboveMinor: 5000,
  },
  professional: {
    ...sharedMandate,
    id: 'mandate-professional-001',
    profile: 'professional',
    capabilities: ['READ_MEMORY', 'WRITE_MEMORY', 'CREATE_MEDIA', 'PUBLISH_CONTENT', 'BUY_SERVICE', 'RECEIVE_MONEY', 'LICENSE_KNOWLEDGE', 'CONTACT_PERSON', 'MANAGE_ASSET'],
    allowedPurposes: ['Generate campaign video', 'Produce podcast episode', 'Publish approved research note', 'License verified knowledge'],
  },
};

export const proposedActions: Record<Profile, ProposedAction[]> = {
  personal: [
    {
      id: 'personal-a1', principalId: 'human-owner', profile: 'personal', capability: 'BUY_SERVICE',
      purpose: 'Renew essential subscription', amountMinor: 1290, currency: 'NOK', recipient: 'openai',
      channel: 'private', risk: 'low', reversible: true, external: false,
      dataScopes: ['payment-metadata'], evidence: ['active-subscription', 'budget-available'],
    },
    {
      id: 'personal-a2', principalId: 'human-owner', profile: 'personal', capability: 'BOOK_SERVICE',
      purpose: 'Book approved travel', amountMinor: 14900, currency: 'NOK', recipient: 'unknown-airline',
      channel: 'private', risk: 'medium', reversible: false, external: true,
      dataScopes: ['calendar-availability'], evidence: ['travel-objective'],
    },
    {
      id: 'personal-a3', principalId: 'unknown-principal', profile: 'personal', capability: 'SPEND_MONEY',
      purpose: 'Buy speculative asset', amountMinor: 250000, currency: 'NOK', recipient: 'unknown',
      channel: 'private', risk: 'critical', reversible: false, external: true,
      dataScopes: ['payment-metadata'], evidence: [],
    },
  ],
  professional: [
    {
      id: 'professional-a1', principalId: 'human-owner', profile: 'professional', capability: 'CREATE_MEDIA',
      purpose: 'Generate campaign video', amountMinor: 4900, currency: 'NOK', recipient: 'higgsfield',
      channel: 'website', risk: 'low', reversible: true, external: false,
      dataScopes: ['content-drafts'], evidence: ['approved-campaign-brief', 'rights-cleared'],
    },
    {
      id: 'professional-a2', principalId: 'human-owner', profile: 'professional', capability: 'PUBLISH_CONTENT',
      purpose: 'Produce podcast episode', amountMinor: 0, currency: 'NOK', recipient: 'spotify',
      channel: 'spotify', risk: 'medium', reversible: false, external: true,
      dataScopes: ['professional-knowledge'], evidence: ['final-audio', 'rights-cleared', 'human-review'],
    },
    {
      id: 'professional-a3', principalId: 'human-owner', profile: 'professional', capability: 'SHARE_DATA',
      purpose: 'Sell private contact data', amountMinor: 0, currency: 'NOK', recipient: 'ad-network',
      channel: 'website', risk: 'critical', reversible: false, external: true,
      dataScopes: ['private-contacts'], evidence: [],
    },
  ],
};

export const vaultItems = [
  { category: 'Identity', count: 8, state: 'Encrypted', access: 'Human only' },
  { category: 'Financial', count: 14, state: 'Connected', access: 'Read + step-up' },
  { category: 'Professional IP', count: 126, state: 'Indexed', access: 'Professional profile' },
  { category: 'Personal memory', count: 2840, state: 'Local-first', access: 'Private profile' },
  { category: 'Consent rules', count: 23, state: 'Active', access: 'Policy engine' },
  { category: 'Credentials', count: 17, state: 'Sealed', access: 'Task-scoped' },
];

export const wikiItems = [
  { title: 'Execution governance', type: 'Knowledge domain', sources: 184, confidence: 0.94, updated: 'Today' },
  { title: 'Professional network map', type: 'Relationship graph', sources: 96, confidence: 0.86, updated: 'Today' },
  { title: 'Product decisions', type: 'Decision history', sources: 73, confidence: 0.91, updated: 'Yesterday' },
  { title: 'Personal operating preferences', type: 'Private memory', sources: 42, confidence: 0.89, updated: '2 days ago' },
];

export const opportunities = [
  { title: 'Convert research archive into a paid executive briefing', value: 89, effort: 34, risk: 18, owner: 'Creator Studio' },
  { title: 'Recover value from unused software subscriptions', value: 67, effort: 12, risk: 8, owner: 'Asset Manager' },
  { title: 'Package governance method as a licensed agent skill', value: 92, effort: 58, risk: 39, owner: 'Knowledge Market' },
  { title: 'Publish weekly cross-platform insight series', value: 78, effort: 27, risk: 22, owner: 'Creator Studio' },
];

export const creatorPipeline = [
  { title: 'Execution boundary explained', format: 'Research note', stage: 'Approved', channels: ['Website', 'LinkedIn'] },
  { title: 'The agent wallet is not a bank account', format: 'Podcast', stage: 'Human review', channels: ['Spotify', 'YouTube'] },
  { title: 'Personal Agency OS launch film', format: 'Video', stage: 'Generating', channels: ['Website', 'YouTube', 'LinkedIn'] },
  { title: 'Ambient agency', format: 'Music', stage: 'Draft', channels: ['Spotify'] },
];

export const assets = [
  { name: 'Research archive', kind: 'Knowledge', estimatedMinor: 360000, status: 'Underused', next: 'Package and license' },
  { name: 'Recorded talks', kind: 'Media', estimatedMinor: 92000, status: 'Unindexed', next: 'Transcribe and repurpose' },
  { name: 'Unused subscriptions', kind: 'Expense', estimatedMinor: 18400, status: 'Recurring waste', next: 'Cancel or renegotiate' },
  { name: 'Professional network', kind: 'Relationship', estimatedMinor: 0, status: 'Dormant signals', next: 'Permissioned activation' },
];

export const attentionRules = [
  { source: 'General advertising', policy: 'Blocked', condition: 'No broad targeting' },
  { source: 'Investor introductions', policy: 'Allowed', condition: 'Relevant stage and thesis match' },
  { source: 'Financial offers', policy: 'Conditional', condition: 'Must improve current terms by ≥ 10%' },
  { source: 'Research participation', policy: 'Compensated', condition: 'Purpose-bound, revocable consent' },
];

export const walletBudgets = [
  { category: 'Compute and models', usedMinor: 8900, limitMinor: 25000 },
  { category: 'Media production', usedMinor: 4900, limitMinor: 20000 },
  { category: 'Distribution', usedMinor: 2400, limitMinor: 10000 },
  { category: 'Human expertise', usedMinor: 8700, limitMinor: 30000 },
];
